import { useEffect } from "react";

/**
 * Remove any previously injected per-tenant dynamic font link
 */
const removeDynamicFontLink = () => {
  const existingLink = document.querySelector('link[data-dynamic-font]');
  if (existingLink) {
    existingLink.remove();
  }
};

/**
 * Load a tenant-supplied custom font from its own URL (CDN, a foundry kit,
 * or a self-hosted @font-face stylesheet — any of these work, this isn't
 * Google-Fonts-specific). Integrity is only applied when the tenant
 * explicitly supplies one, since these URLs are arbitrary and not known
 * ahead of build time. Unlike the app's own bundled default fonts, this is
 * infrastructure the app doesn't control, so a failed load is logged
 * instead of silently falling back to system sans-serif with no signal.
 */
const loadFontCSS = (fontUrl, fontIntegrity, fontName) => {
  removeDynamicFontLink();

  // Create new link element for the tenant's custom font
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = fontUrl;
  link.setAttribute('data-dynamic-font', 'true');
  if (fontIntegrity) {
    link.integrity = fontIntegrity;
    link.crossOrigin = 'anonymous';
  }
  link.onerror = () => {
    // eslint-disable-next-line no-console
    console.error(
      `[useIntegratorFonts] Failed to load font "${fontName}" from ${fontUrl}. ` +
      `Falling back to sans-serif. Check the URL is reachable and, for self-hosted fonts, ` +
      `that the font files are served with an Access-Control-Allow-Origin header (Firefox/Safari ` +
      `enforce CORS on @font-face src fetches even when this <link> itself has none).`
    );
  };

  // Add to document head
  document.head.appendChild(link);
};

/**
 * Apply font globally via CSS variable
 */
const applyGlobalFont = (fontName) => {
  // Set CSS variable that all font classes will use
  document.documentElement.style.setProperty('--primary-font', `"${fontName}", sans-serif`);

  // Also directly set font-family on root and body for immediate effect
  document.documentElement.style.setProperty('font-family', `"${fontName}", sans-serif`);
  document.body.style.setProperty('font-family', `"${fontName}", sans-serif`);
};

/**
 * Directly override the font-family property for all font classes
 * This is necessary if your CSS classes aren't using the CSS variable
 */
const updateFontClassStyles = (fontName) => {
  // Create a style element
  const style = document.createElement('style');
  style.setAttribute('id', 'dynamic-font-styles');

  // Remove any existing style element
  const existingStyle = document.getElementById('dynamic-font-styles');
  if (existingStyle) {
    existingStyle.remove();
  }

  // Create CSS content that overrides all font classes and applies globally
  const cssContent = `
    :root, body, * {
      font-family: "${fontName}", sans-serif !important;
    }

    .font-Inter {
      font-family: "${fontName}", sans-serif !important;
    }
  `;

  // Add the styles to the style element
  style.textContent = cssContent;

  // Add to document head
  document.head.appendChild(style);
};

export const useIntegratorFonts = (integratorTheme) => {
  useEffect(() => {
    if (!integratorTheme) return;

    // Get font information from theme
    const fontName = integratorTheme?.font_family || 'Inter';
    // Only a genuine per-tenant override needs an external font — the default
    // (Inter) is self-hosted via @fontsource, so no CDN request should fire for it.
    const fontUrl = integratorTheme?.font_url;

    if (fontUrl) {
      const fontIntegrity =
        integratorTheme?.font_integrity ||
        integratorTheme?.font_sri ||
        integratorTheme?.website_font_integrity;

      loadFontCSS(fontUrl, fontIntegrity, fontName);
    } else {
      removeDynamicFontLink();
    }

    // Apply the font globally via CSS variable
    applyGlobalFont(fontName);

    // IMPORTANT: Also override the actual font classes directly
    updateFontClassStyles(fontName);

    // Clean up the injected <link>/<style> if this component unmounts, so a
    // shorter-lived caller (e.g. a live theme-preview panel) doesn't leak
    // stale font overrides into whatever mounts next.
    return () => {
      removeDynamicFontLink();
      document.getElementById('dynamic-font-styles')?.remove();
    };
  }, [integratorTheme]);
};
